import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Monitor, Moon, Sun } from 'lucide-react';
import { api, type MeResponse } from '@/lib/api';
import { getStoredTheme, setTheme } from '@/lib/theme';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SoftAccessBanner } from '@/components/SoftAccessBanner';
import { cn } from '@/lib/utils';

export default function Profil() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/auth/me'),
  });

  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; msg: string } | null>(null);

  async function setPassword() {
    if (pw.length < 8) {
      setStatus({ kind: 'error', msg: 'Passwort muss mindestens 8 Zeichen lang sein.' });
      return;
    }
    if (pw !== pw2) {
      setStatus({ kind: 'error', msg: 'Die Passwörter stimmen nicht überein.' });
      return;
    }
    const hasPw = me.data?.user?.hasPassword ?? false;
    if (hasPw && cur.length === 0) {
      setStatus({ kind: 'error', msg: 'Bitte gib dein aktuelles Passwort ein.' });
      return;
    }
    try {
      // Changing an existing password requires the current one (re-auth);
      // first-time set sends only the new password.
      await api.post(
        '/api/auth/set-password',
        hasPw ? { password: pw, currentPassword: cur } : { password: pw },
      );
      setStatus({ kind: 'ok', msg: 'Passwort gespeichert.' });
      setCur('');
      setPw('');
      setPw2('');
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      const msg =
        code === 'invalid_current_password'
          ? 'Aktuelles Passwort ist falsch.'
          : code === 'rate_limited' || code === 'too_many_attempts'
            ? 'Zu viele Versuche. Bitte versuche es später erneut.'
            : 'Konnte Passwort nicht speichern.';
      setStatus({ kind: 'error', msg });
    }
  }

  async function logout() {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // even if the server call fails, still drop the client cache below
    }
    qc.removeQueries({ queryKey: ['me'] });
    qc.clear();
    // Hard navigation — wipes any cached DOM state from bfcache and forces a
    // clean me re-check on the next page.
    window.location.replace('/login');
  }

  if (me.isLoading) return <div className="p-6 text-muted-foreground">Lädt …</div>;
  const user = me.data?.user;
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6 pb-40">
      <h1 className="text-2xl font-semibold">Profil</h1>

      <Card>
        <CardHeader>
          <CardTitle>Konto</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <span className="text-muted-foreground">E-Mail: </span>
            <span>{user.email}</span>
          </div>
          {user.name && (
            <div>
              <span className="text-muted-foreground">Name: </span>
              <span>{user.name}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {user.soft ? (
        <Card>
          <CardHeader>
            <CardTitle>Zugang sichern</CardTitle>
          </CardHeader>
          <CardContent>
            <SoftAccessBanner email={user.email} />
          </CardContent>
        </Card>
      ) : (
      <Card>
        <CardHeader>
          <CardTitle>{user.hasPassword ? 'Passwort ändern' : 'Passwort einrichten'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void setPassword();
            }}
          >
            <p className="text-sm text-muted-foreground">
              Optional. Du kannst dich weiter über den Login-Code per E-Mail einloggen.
            </p>
            {user.hasPassword && (
              <div className="space-y-1.5">
                <Label htmlFor="curpw">Aktuelles Passwort</Label>
                <Input id="curpw" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="pw">Neues Passwort</Label>
              <Input id="pw" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw2">Wiederholen</Label>
              <Input id="pw2" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
            </div>
            <Button type="submit" disabled={!pw || !pw2 || pw !== pw2 || pw.length < 8 || (user.hasPassword && !cur)}>Speichern</Button>
            {status && (
              <p className={status.kind === 'ok' ? 'text-sm text-success' : 'text-sm text-destructive'}>
                {status.msg}
              </p>
            )}
          </form>
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Erscheinungsbild</CardTitle>
        </CardHeader>
        <CardContent>
          <ThemePicker />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Abmelden</CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={logout}>Abmelden</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ThemePicker() {
  const [theme, setLocal] = useState(getStoredTheme());
  useEffect(() => {
    const onChange = () => setLocal(getStoredTheme());
    window.addEventListener('storage', onChange);
    return () => window.removeEventListener('storage', onChange);
  }, []);
  const options = [
    { value: 'light' as const, label: 'Hell', icon: Sun },
    { value: 'dark' as const, label: 'Dunkel', icon: Moon },
    { value: 'system' as const, label: 'System', icon: Monitor },
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {options.map((o) => {
        const Icon = o.icon;
        const active = theme === o.value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => {
              setTheme(o.value);
              setLocal(o.value);
            }}
            className={cn(
              'flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition-colors',
              active
                ? 'border-primary bg-primary/10 text-foreground'
                : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <Icon className="size-4" />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
